import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  Settings,
  Camera,
  User,
  Calendar,
  Hash,
  Loader2,
  Play
} from 'lucide-react';
// import { ocrService, PyImgResult } from '@/services/ocrService-original';
import { getApiBaseUrl } from '@/config/production';

export interface PhotoMetadata {
  originalFilename: string;
  cameraModel: string;
  resolution: string;
  qualityScore: number;
  bibNumbers: string[]; // Único campo manual editável
  athleteIds: string[]; // Mantido para compatibilidade, mas não será usado
  tags: string[]; // Mantido para compatibilidade, mas não será usado
  eventIds: string[]; // Mantido para compatibilidade, mas não será usado
  photographerId?: string;
  technical?: {
    format?: string;
    mode?: string;
    width?: number;
    height?: number;
    orientation?: number;
    color?: string;
    bits_per_channel?: number;
    dpi?: number[] | null;
    file_size_bytes?: number;
    capture_datetime?: string | null;
    gps_latitude?: number | null;
    gps_longitude?: number | null;
    camera?: string | null;
    camera_make?: string | null;
    author?: string | null;
  };
}

interface MetadataFormProps {
  photo: File;
  onSave: (metadata: PhotoMetadata) => void;
  onTextExtracted?: (text: string) => void;
  initialData?: Partial<PhotoMetadata>;
  cachedMetadata?: PhotoMetadata;
  isAutoAnalyzing?: boolean;
  onRefreshAnalysis?: () => void;
}

export const MetadataForm = ({
  photo,
  onSave,
  onTextExtracted,
  initialData,
  cachedMetadata,
  isAutoAnalyzing = false,
  onRefreshAnalysis
}: MetadataFormProps) => {
  const { toast } = useToast();
  const [metadata, setMetadata] = useState<PhotoMetadata>({
    originalFilename: photo.name,
    cameraModel: '',
    resolution: '',
    qualityScore: 0,
    bibNumbers: initialData?.bibNumbers || [], // Inicializar com dados existentes
    athleteIds: [],
    tags: [],
    eventIds: [],
    photographerId: '',
    technical: undefined,
    ...initialData
  });

  // Atualizar metadata quando initialData ou cachedMetadata mudar
  useEffect(() => {

    // Sempre usar cachedMetadata se disponível, pois é mais atualizado
    if (cachedMetadata) {
      setMetadata(cachedMetadata);
      setProcessed(true);

      // ✅ Preencher technicalData automaticamente se disponível
      if (cachedMetadata.technical) {
        setTechnicalData(cachedMetadata.technical);
      }
    } else if (initialData) {
      // Usar dados iniciais se não há cache
      setMetadata(prev => ({
        ...prev,
        ...initialData
      }));

      // ✅ Preencher technicalData se disponível nos dados iniciais
      if (initialData.technical) {
        setTechnicalData(initialData.technical);
      }
    }
  }, [initialData, cachedMetadata, photo.name]);

  const [processing, setProcessing] = useState(false);
  const [processed, setProcessed] = useState(false);
  const [selectedOCRProvider, setSelectedOCRProvider] = useState<string>('devonno-pyimg-native-nodejs');
  const [technicalData, setTechnicalData] = useState<any>(null);
  const [newBibNumber, setNewBibNumber] = useState('');
  const [showProviderCombo, setShowProviderCombo] = useState(false);


  const handleProcess = async () => {
    if (selectedOCRProvider === 'pyimg') {
      await processWithPyImg();
    } else if (selectedOCRProvider === 'pyimg-complete') {
      await processWithPyImgComplete();
    } else if (selectedOCRProvider === 'devonno-pyimg-native-nodejs') {
      await processWithNodeJsNative();
    } else {
      // Processamento mock para desenvolvimento
      setProcessing(true);
      setTimeout(() => {
        const mockMetadata = {
          ...metadata,
          cameraModel: 'Canon EOS R5',
          resolution: '8192x5464',
          qualityScore: 0.95,
          bibNumbers: ['12345', '67890'],
          tags: ['test', 'mock'],
          technical: {
            format: 'JPEG',
            mode: 'RGB',
            width: 8192,
            height: 5464,
            orientation: 1,
            color: 'sRGB',
            bits_per_channel: 8,
            dpi: [300, 300],
            file_size_bytes: 2048576,
            capture_datetime: '2024-01-15 10:30:00',
            gps_latitude: null,
            gps_longitude: null,
            camera: 'Canon EOS R5',
            camera_make: 'Canon',
            author: 'João Silva Fotógrafo'
          }
        };
        setTechnicalData(mockMetadata.technical);
        setMetadata(mockMetadata);
        setProcessed(true);
        onSave(mockMetadata);
        toast({
          title: "Processamento concluído",
          description: "Metadados processados com sucesso",
        });
        setProcessing(false);
      }, 2000);
    }
  };

  const processWithPyImg = async () => {
    try {
      setProcessing(true);

      // Usar rota unificada com provider=python (padrão)
      const formData = new FormData();
      formData.append('image', photo);

      const response = await fetch(`${getApiBaseUrl()}/api/pyimg/analyze?provider=python`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.data?.technical) {
        throw new Error(result.error || 'Resposta inválida do PyImg');
      }

      const technicalData = result.data.technical;
      setTechnicalData(technicalData);

      const cm = technicalData.camera || technicalData.camera_make || '';
      const w = technicalData.width || 0;
      const h = technicalData.height || 0;

      const updatedMetadata = {
        ...metadata,
        cameraModel: cm,
        resolution: w && h ? `${w}x${h}` : '',
        qualityScore: 0.95,
        technical: technicalData
      };

      setMetadata(updatedMetadata);
      setProcessed(true);
      onSave(updatedMetadata);

      if (technicalData.author && onTextExtracted) {
        onTextExtracted(technicalData.author);
      }

      // Toast removido - análise não precisa notificar o usuário
      console.log('✅ Análise PyImg concluída:', {
        width: technicalData.width,
        height: technicalData.height,
        format: technicalData.format,
        camera: technicalData.camera,
        provider: 'Python',
        warnings: result.data.warnings
      });

    } catch (error) {
      console.error('❌ Erro na análise PyImg:', error);
      // Toast removido - erro de análise não precisa notificar o usuário
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      console.error('📊 Detalhes técnicos da análise PyImg:', {
        fileName: photo.name,
        fileSize: photo.size,
        fileType: photo.type,
        error: errorMessage,
        stack: errorStack
      });
    } finally {
      setProcessing(false);
    }
  };

  const processWithPyImgComplete = async () => {
    try {
      setProcessing(true);

      // Usar rota unificada com provider=python (padrão)
      const formData = new FormData();
      formData.append('image', photo);

      const response = await fetch(`${getApiBaseUrl()}/api/pyimg/analyze?provider=python`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.data?.technical) {
        throw new Error(result.error || 'Resposta inválida do PyImg');
      }

      const technicalData = result.data.technical;
      setTechnicalData(technicalData);

      const cm = technicalData.camera || technicalData.camera_make || '';
      const w = technicalData.width || 0;
      const h = technicalData.height || 0;

      const updatedMetadata = {
        ...metadata,
        cameraModel: cm,
        resolution: w && h ? `${w}x${h}` : '',
        qualityScore: 0.95,
        technical: technicalData
      };

      setMetadata(updatedMetadata);
      setProcessed(true);
      onSave(updatedMetadata);

      if (technicalData.author && onTextExtracted) {
        onTextExtracted(technicalData.author);
      }


    } catch (error) {
      console.error('❌ Erro na análise PyImg Completa:', error);
      // Toast removido - erro de análise não precisa notificar o usuário
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      console.error('📊 Detalhes técnicos da análise PyImg Completa:', {
        fileName: photo.name,
        fileSize: photo.size,
        fileType: photo.type,
        error: errorMessage,
        stack: errorStack
      });
    } finally {
      setProcessing(false);
    }
  };

  const processWithNodeJsNative = async () => {
    setProcessing(true);
    try {
      console.log('🔧 Iniciando análise NodeJS nativa:', photo.name);

      // Usar rota unificada com provider=nodejs
      const formData = new FormData();
      formData.append('image', photo);

      const response = await fetch(`${getApiBaseUrl()}/api/pyimg/analyze?provider=nodejs`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.data?.technical) {
        throw new Error(result.error || 'Resposta inválida do NodeJS');
      }

      const technicalData = result.data.technical;
      setTechnicalData(technicalData);

      const cm = technicalData.camera || technicalData.camera_make || '';
      const w = technicalData.width || 0;
      const h = technicalData.height || 0;

      const updatedMetadata = {
        ...metadata,
        cameraModel: cm,
        resolution: w && h ? `${w}x${h}` : '',
        qualityScore: 0.95,
        technical: technicalData
      };

      setMetadata(updatedMetadata);
      setProcessed(true);
      onSave(updatedMetadata);

      if (technicalData.author && onTextExtracted) {
        onTextExtracted(technicalData.author);
      }



    } catch (error) {
      console.error('❌ Erro na análise NodeJS nativa:', error);
      // Toast removido - erro de análise não precisa notificar o usuário
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      console.error('📊 Detalhes técnicos da análise NodeJS:', {
        fileName: photo.name,
        fileSize: photo.size,
        fileType: photo.type,
        error: errorMessage,
        stack: errorStack
      });
    } finally {
      setProcessing(false);
    }
  };

  const addBibNumber = () => {
    if (newBibNumber.trim() && !metadata.bibNumbers.includes(newBibNumber.trim())) {
      const updatedMetadata = {
        ...metadata,
        bibNumbers: [...metadata.bibNumbers, newBibNumber.trim()]
      };
      setMetadata(updatedMetadata);
      setNewBibNumber('');

      // Salvar no cache através do onSave
      onSave(updatedMetadata);
    }
  };

  const removeBibNumber = (bibToRemove: string) => {
    const updatedMetadata = {
      ...metadata,
      bibNumbers: metadata.bibNumbers.filter(bib => bib !== bibToRemove)
    };
    setMetadata(updatedMetadata);

    // Salvar no cache através do onSave
    onSave(updatedMetadata);
  };

  return (
    <Card className="h-full bg-white shadow-sm border border-gray-200">
      <CardHeader className="bg-gray-50 border-b border-gray-200 py-4">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Settings className="h-4 w-4 text-slate-600" />
            <span className="text-slate-800 font-medium text-sm">Metadados da Foto</span>
          </div>

          {/* Botão discreto para controlar visibilidade das configurações */}
          <Button
            onClick={() => setShowProviderCombo(!showProviderCombo)}
            variant="ghost"
            size="sm"
            title={showProviderCombo ? "Ocultar configurações" : "Mostrar configurações"}
            className="h-7 w-7 p-0 hover:bg-slate-200 text-slate-600"
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4 p-6">

        {/* Provedor de Análise - Oculto por padrão */}
        {showProviderCombo && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="space-y-2">
              <Label className="flex items-center space-x-2">
                <Settings className="h-4 w-4" />
                <span>Provedor de Análise</span>
              </Label>
              <Select value={selectedOCRProvider} onValueChange={setSelectedOCRProvider}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o provedor de análise" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pyimg">Devonno PyImg Python - Simples</SelectItem>
                  <SelectItem value="pyimg-complete">Devonno PyImg Python - Completa</SelectItem>
                  <SelectItem value="devonno-pyimg-native-nodejs">Devonno PyImg Native NodeJS</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {selectedOCRProvider === 'pyimg'
                  ? 'Análise de metadados técnicos básicos (autor, câmera, resolução, data) - Python'
                  : selectedOCRProvider === 'pyimg-complete'
                    ? 'Análise completa com metadados técnicos, OCR, detecção de rostos e marcações interativas - Python'
                    : selectedOCRProvider === 'devonno-pyimg-native-nodejs'
                      ? 'Análise nativa NodeJS - Metadados EXIF, dimensões, câmera, autor, GPS (sem Python)'
                      : 'Selecione um provedor de análise'
                }
              </p>
            </div>

            {/* Botões de ação */}
            <div className="flex space-x-2 pt-2 border-t border-gray-200">
              <Button
                onClick={handleProcess}
                disabled={processing || isAutoAnalyzing}
                className="flex-1"
              >
                {processing || isAutoAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {isAutoAnalyzing ? 'Analisando...' : 'Processando...'}
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    {processed ? 'Atualizar' : 'Processar Foto'}
                  </>
                )}
              </Button>

              {onRefreshAnalysis && (
                <Button
                  onClick={onRefreshAnalysis}
                  disabled={processing || isAutoAnalyzing}
                  variant="outline"
                  size="sm"
                  title="Atualizar análise automática"
                >
                  <Loader2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}



        {/* Metadados da Análise (Readonly) */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Camera className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Câmera/Modelo:</span>
              </div>
              <span className="text-sm font-semibold text-blue-900">
                {technicalData?.camera || technicalData?.camera_make || 'Não disponível'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Autor da Foto:</span>
              </div>
              <span className="text-sm font-semibold text-blue-900">
                {technicalData?.author || 'Não disponível'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Data de Captura:</span>
              </div>
              <span className="text-sm font-semibold text-blue-900">
                {technicalData?.capture_datetime || 'Não disponível'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Hash className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Resolução:</span>
              </div>
              <span className="text-sm font-semibold text-blue-900">
                {technicalData?.width && technicalData?.height
                  ? `${technicalData.width} x ${technicalData.height}`
                  : 'Não disponível'
                }
              </span>
            </div>

            {/* Mensagem discreta quando dados não estão disponíveis */}
            {(!technicalData ||
              (!technicalData.camera && !technicalData.camera_make &&
                !technicalData.author &&
                !technicalData.capture_datetime &&
                !technicalData.width && !technicalData.height)) && (
                <div className="mt-3 p-2 bg-gray-50 rounded border border-gray-200">
                  <p className="text-xs text-gray-600 text-center">
                    ℹ️ Dados de análise não disponíveis - Python não configurado
                  </p>
                </div>
              )}
          </div>
        </div>

        {/* Campo Editável - Apenas Bib Numbers */}
        <div className="space-y-4 mt-6">
          <h3 className="text-sm font-medium text-slate-800 font-semibold">Informações Manuais</h3>

          {/* Bib Numbers */}
          <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg border border-primary/20 shadow-sm">
            <Label className="flex items-center space-x-2 mb-3">
              <Hash className="h-4 w-4 text-primary" />
              <span className="text-foreground font-semibold">Bib Numbers</span>
            </Label>
            <div className="flex space-x-2 mt-1">
              <Input
                value={newBibNumber}
                onChange={(e) => setNewBibNumber(e.target.value)}
                placeholder="Digite um número da bib"
                className="bg-background border-border focus:border-primary focus:ring-primary"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addBibNumber();
                  }
                }}
              />
              <Button type="button" onClick={addBibNumber} size="sm" className="bg-primary hover:bg-secondary text-primary-foreground">
                Adicionar
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {metadata.bibNumbers.map((bib, index) => (
                <Badge
                  key={index}
                  className={`cursor-pointer transition-colors bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 ${processed ? 'bg-primary text-primary-foreground hover:bg-secondary' : ''
                    }`}
                  onClick={() => removeBibNumber(bib)}
                >
                  {bib} ×
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


